/**
 * Booking classification, sorting and payload construction.
 *
 * Extracted from `renderPatientAppointments`, `switchPatientAppointmentsTab`,
 * `renderScheduleManagement`, `isOverdue`, `isComingSoon` and the two rival
 * copies of `saveManualBooking`.
 */

import { todayKey, timeToMinutes, formatTime12, arabicDayOf } from './dates';
import { BOOKING_TYPES } from './pricing';

export const BOOKING_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  IN_PROGRESS: 'in_progress',
};

/** Arabic labels for statuses, as shown in the live UI. */
export const STATUS_LABELS = {
  [BOOKING_STATUS.PENDING]: 'قيد الانتظار',
  [BOOKING_STATUS.CONFIRMED]: 'مؤكد',
  [BOOKING_STATUS.COMPLETED]: 'مكتمل',
  [BOOKING_STATUS.CANCELLED]: 'ملغي',
  [BOOKING_STATUS.IN_PROGRESS]: 'جاري الكشف',
};

/** Tailwind classes per status, so badges look the same everywhere. */
export const STATUS_STYLES = {
  [BOOKING_STATUS.PENDING]: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  [BOOKING_STATUS.CONFIRMED]: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  [BOOKING_STATUS.COMPLETED]: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30',
  [BOOKING_STATUS.CANCELLED]: 'bg-red-500/10 text-red-300 border-red-500/30',
  [BOOKING_STATUS.IN_PROGRESS]: 'bg-purple-500/10 text-purple-300 border-purple-500/30',
};

export const statusLabel = (status) => STATUS_LABELS[status] || status || '—';
export const statusStyle = (status) =>
  STATUS_STYLES[status] || 'bg-slate-500/10 text-slate-300 border-slate-500/30';

export const typeLabel = (type) =>
  type === BOOKING_TYPES.FOLLOWUP ? 'متابعة' : 'كشف';

/* ==========================================================================
   Time classification
   ========================================================================== */

/** A booking is past once its date is behind today, or its slot has elapsed today. */
export function isPastBooking(booking, now = new Date()) {
  if (!booking?.date) return false;
  const today = todayKey(now);
  if (booking.date < today) return true;
  if (booking.date > today) return false;
  const slotMinutes = timeToMinutes(booking.time);
  if (Number.isNaN(slotMinutes)) return false;
  return slotMinutes < now.getHours() * 60 + now.getMinutes();
}

/** Legacy `isOverdue`: a date that has already gone by. */
export function isOverdue(dateKey, now = new Date()) {
  if (!dateKey) return false;
  return dateKey < todayKey(now);
}

/** Legacy `isComingSoon`: within the next `windowDays` days (default 3). */
export function isComingSoon(dateKey, now = new Date(), windowDays = 3) {
  if (!dateKey) return false;
  const today = todayKey(now);
  if (dateKey < today) return false;
  const limit = new Date(now);
  limit.setDate(limit.getDate() + windowDays);
  return dateKey <= todayKey(limit);
}

/**
 * The three tabs the patient's appointments view shows.
 * @returns {'upcoming'|'pending'|'past'}
 */
export function bookingBucket(booking, now = new Date()) {
  if (booking?.status === BOOKING_STATUS.CANCELLED) return 'past';
  if (booking?.status === BOOKING_STATUS.COMPLETED) return 'past';
  if (isPastBooking(booking, now)) return 'past';
  if (booking?.status === BOOKING_STATUS.PENDING) return 'pending';
  return 'upcoming';
}

/** Partition a list into the three buckets in one pass. */
export function groupBookingsByBucket(bookings = [], now = new Date()) {
  const groups = { upcoming: [], pending: [], past: [] };
  for (const booking of bookings) groups[bookingBucket(booking, now)].push(booking);
  Object.values(groups).forEach((list) => list.sort(compareBookingsChronologically));
  groups.past.reverse(); // newest first for history
  return groups;
}

/** Ascending by date then time — the order every calendar and queue uses. */
export function compareBookingsChronologically(a, b) {
  if (a.date !== b.date) return String(a.date).localeCompare(String(b.date));
  const aMin = timeToMinutes(a.time);
  const bMin = timeToMinutes(b.time);
  if (Number.isNaN(aMin) || Number.isNaN(bMin)) {
    return String(a.time).localeCompare(String(b.time));
  }
  return aMin - bMin;
}

/* ==========================================================================
   Filtering
   ========================================================================== */

export const bookingsForPatient = (bookings = [], patientId) =>
  bookings.filter((b) => String(b.patientId) === String(patientId));

export const bookingsForDoctor = (bookings = [], doctorId) =>
  bookings.filter((b) => String(b.doctorId) === String(doctorId));

export const bookingsForDate = (bookings = [], dateKey) =>
  bookings.filter((b) => b.date === dateKey);

export const bookingsForClinic = (bookings = [], clinicId) =>
  bookings.filter((b) => String(b.clinicId) === String(clinicId));

/** Group by date key -> booking[], each list chronologically sorted. */
export function groupBookingsByDate(bookings = []) {
  const byDate = new Map();
  for (const booking of bookings) {
    if (!byDate.has(booking.date)) byDate.set(booking.date, []);
    byDate.get(booking.date).push(booking);
  }
  for (const list of byDate.values()) list.sort(compareBookingsChronologically);
  return byDate;
}

/* ==========================================================================
   Deposit state
   ========================================================================== */

/**
 * Where a booking sits in the deposit workflow.
 * @returns {'none'|'awaiting_receipt'|'awaiting_review'|'rejected'|'confirmed'}
 */
export function depositState(booking) {
  if (!booking?.depositRequired) return 'none';
  if (booking.depositConfirmed) return 'confirmed';
  if (booking.depositRejectionReason) return 'rejected';
  if (booking.depositPaid && booking.depositReceipt) return 'awaiting_review';
  return 'awaiting_receipt';
}

export const DEPOSIT_STATE_LABELS = {
  none: 'لا يتطلب عربون',
  awaiting_receipt: 'بانتظار رفع الإيصال',
  awaiting_review: 'بانتظار مراجعة العيادة',
  rejected: 'تم رفض الإيصال',
  confirmed: 'تم تأكيد العربون',
};

/* ==========================================================================
   Payload construction
   ========================================================================== */

/**
 * Build the exact body POST /api/bookings/create expects.
 *
 * The backend recomputes `price`, `originalPrice`, `depositPercent` and
 * `depositAmount` from the clinic, so those are sent for parity only; the field
 * that genuinely carries client intent is `finalPrice`, which the backend keeps.
 *
 * The id is a millisecond timestamp string because the Booking model's primary
 * key is a STRING and the whole system — including reschedule and prescription
 * linkage — assumes that format.
 */
export function buildBookingPayload({
  doctor,
  clinic,
  patient,
  dateKey,
  time,
  bookingType = BOOKING_TYPES.EXAM,
  quote,
  voucherCode = null,
  depositReceipt = null,
  bookedBy = 'patient',
  source = 'web',
  manualPatientCode = null,
  manualPatientName = null,
  id = null,
}) {
  const deposit = quote?.deposit || { required: false, percent: 0, amount: 0, instaPayAccount: '' };

  return {
    id: id || String(Date.now()),
    doctorId: doctor?.id ?? null,
    doctorName: doctor?.name ?? null,
    patientId: patient?.id ?? null,
    patientName: patient?.name || manualPatientName || 'مريض',
    patientPhone: patient?.phone || patient?.username || '',
    clinicId: clinic?.id ?? null,
    clinicName: clinic?.name ?? null,
    date: dateKey,
    time,
    type: bookingType,
    bookingType,
    // A clinic that takes a deposit holds the booking as pending until the
    // receipt is reviewed; everything else is confirmed on the spot.
    status: deposit.required ? BOOKING_STATUS.PENDING : BOOKING_STATUS.CONFIRMED,

    // The consultation figures. Clinic extras are carried separately in
    // `selectedServices` and added by the backend, which reprices them from
    // its own tables — a price posted from a browser is not evidence of what
    // a service costs.
    originalPrice: quote?.basePrice ?? 0,
    price: quote?.basePrice ?? 0,
    finalPrice: quote?.finalPrice ?? 0,
    lockedFinalPrice: quote?.finalPrice ?? 0, // survives a reschedule
    selectedServices: (quote?.services ?? []).map((service) => ({
      id: service.id,
      name: service.name,
      price: service.price,
    })),
    discountAmount: quote?.discountAmount ?? 0,
    lockedDayDiscount: quote?.breakdown?.day ?? 0,
    lockedNightDiscount: quote?.breakdown?.night ?? 0,
    adminDiscount: quote?.breakdown?.admin ?? 0,
    voucherDiscount: quote?.breakdown?.voucher ?? 0,
    discountCode: quote?.breakdown?.voucher ? voucherCode : null,
    voucherCode: quote?.breakdown?.voucher ? voucherCode : null,

    depositRequired: deposit.required,
    depositPercent: Math.round(deposit.percent) || 0,
    depositAmount: Math.round(deposit.amount) || 0,
    instaPayAccount: deposit.instaPayAccount || '',
    depositReceipt: depositReceipt || null,
    depositPaid: Boolean(depositReceipt),
    depositConfirmed: false,

    manualPatientCode,
    manualPatientName,
    bookedBy,
    source,
  };
}

/** One-line human summary used in toasts and notifications. */
export function describeBooking(booking) {
  if (!booking) return '';
  const day = arabicDayOf(booking.date);
  return `${typeLabel(booking.type)} — ${day} ${booking.date} الساعة ${formatTime12(booking.time)}`;
}

/**
 * A fingerprint of a list of bookings: equal exactly when nothing a screen
 * shows has changed. The live views poll every few seconds and most polls
 * return the same rows; comparing fingerprints lets them skip the re-render
 * (and the reset of anything keyed to a booking) when nothing moved.
 */
export function bookingsSignature(bookings) {
  if (!Array.isArray(bookings)) return '';
  return JSON.stringify(
    [...bookings]
      .map((b) => (b && typeof b === 'object' ? b : {}))
      .sort((a, b) => String(a.id).localeCompare(String(b.id))),
  );
}

